import { AnimationEvent, animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  DoCheck,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { IconProviderService } from '../../../../whitelabel/services/icon-provider.service';
import { Message } from '../../../contract/message.contract';
import { MessageService } from '../../../services/message.service';
import { ToastPosition } from './contract/toast-position.contract';
import { IndexUtils } from './utils/index-utils';

const DEFAULT_MODAL_Z_INDEX = 1100;

@Component({
  selector: 'osp-toast',
  templateUrl: './toast.component.html',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './toast.component.scss',
  animations: [
    trigger('messageState', [
      state(
        'visible',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      transition('void => *', [
        style({
          transform: '{{showTransformParams}}',
          opacity: 0,
        }),
        animate('{{showTransitionParams}}'),
      ]),
      transition('* => void', [
        animate(
          '{{hideTransitionParams}}',
          style({
            height: 0,
            opacity: 0,
            transform: '{{hideTransformParams}}',
          }),
        ),
      ]),
    ]),
    trigger('lifebarState', [
      state(
        'void',
        style({
          width: '100%',
        }),
      ),
      state(
        '*',
        style({
          width: '0%',
        }),
      ),
      transition('void => *', []),
    ]),
  ],
})
export class ToastComponent implements OnInit, OnDestroy, DoCheck {
  @HostBinding('class') protected hostClass = 'osp-toast';

  @ViewChild('container') protected containerViewChild?: ElementRef<HTMLElement>;
  @ContentChild(TemplateRef) template!: TemplateRef<unknown>;

  @Input() key?: string;
  @Input() life = 3000;
  @Input() cssClass = '';
  @Input() position: ToastPosition = 'top-right';
  @Input() preventOpenDuplicates = false;
  @Input() preventDuplicates = false;
  @Input() showTransformOptions = 'translateX(100%)';
  @Input() hideTransformOptions = 'translateY(-100%)';
  @Input() showTransitionOptions = '300ms ease-out';
  @Input() hideTransitionOptions = '250ms ease-in';

  protected toastMessages: Message[] | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly iconProviderService = inject(IconProviderService);

  private messageArchive?: Message[];
  private readonly defaultMessageLifeMs = 3000;
  private readonly lifebarTransitionOffsetMs = 500;
  private readonly timeoutByMessage = new Map<Message, ReturnType<typeof setTimeout>>();
  private readonly baseZIndex = 0;
  private readonly autoZIndex = true;

  get messages(): Message[] | undefined | null {
    return this.toastMessages;
  }

  set messages(messages: Message[] | undefined | null) {
    this.clearTimeouts();
    this.toastMessages = [...(messages ?? [])];
    this.toastMessages.forEach((message) => this.initTimeout(message));
    this.cdr.markForCheck();
  }

  protected get messageAnimationState(): {
    value: 'visible';
    params: {
      showTransformParams: string;
      hideTransformParams: string;
      showTransitionParams: string;
      hideTransitionParams: string;
    };
  } {
    return {
      value: 'visible',
      params: {
        showTransformParams: this.showTransformOptions,
        hideTransformParams: this.hideTransformOptions,
        showTransitionParams: this.showTransitionOptions,
        hideTransitionParams: this.hideTransitionOptions,
      },
    };
  }

  protected getCloseFunction(message: Message): (event: Event) => void {
    return (event: Event) => this.onCloseIconClick(event, message);
  }

  protected getLifebarTransition(message: Message): string {
    const transitionDuration = Math.max(
      (message.life ?? this.life ?? this.defaultMessageLifeMs) - this.lifebarTransitionOffsetMs,
      0,
    );
    return `width ${transitionDuration}ms linear`;
  }

  protected onMessageMouseEnter(message: Message): void {
    this.clearTimeout(message);
  }

  protected onMessageMouseLeave(message: Message): void {
    this.initTimeout(message);
  }

  protected onCloseIconClick(event: Event, message: Message): void {
    this.removeMessage(message);
    event.preventDefault();
  }

  protected onMessageAnimationStart(event: AnimationEvent): void {
    if (event.fromState !== 'void') {
      return;
    }
    this.setContainerZIndex();
  }

  protected onMessageAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'void' && !this.toastMessages?.length) {
      this.clearContainerZIndex();
    }
  }

  protected getDefaultIcon(message: Message): string {
    switch (this.getSeverity(message)) {
      case 'success':
        return this.iconProviderService.getIcon(IconKey.GlobalApply);
      case 'info':
        return this.iconProviderService.getIcon(IconKey.GlobalInfo);
      case 'error':
        return this.iconProviderService.getIcon(IconKey.GlobalExclamation);
      case 'warn':
        return this.iconProviderService.getIcon(IconKey.GlobalExclamation);
      default:
        return '';
    }
  }

  protected get defaultCloseIcon(): string {
    return this.iconProviderService.getIcon(IconKey.GlobalClose);
  }

  protected get closeAriaLabel(): string | undefined {
    return undefined;
  }

  protected getPositionClass(): string {
    switch (this.position) {
      case 'top-left':
        return 'osp-toast__container--top-left';
      case 'top-center':
        return 'osp-toast__container--top-center';
      case 'bottom-left':
        return 'osp-toast__container--bottom-left';
      case 'bottom-center':
        return 'osp-toast__container--bottom-center';
      case 'bottom-right':
        return 'osp-toast__container--bottom-right';
      case 'center':
        return 'osp-toast__container--center';
      case 'top-right':
      default:
        return 'osp-toast__container--top-right';
    }
  }

  protected getSeverity(message: Message): string {
    return message.severity ?? 'info';
  }

  ngOnInit(): void {
    this.messageService.messageObserver$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((messages) => {
      if (!messages) {
        return;
      }

      if (Array.isArray(messages)) {
        const filteredMessages = messages.filter((message) => this.canAdd(message));
        this.add(filteredMessages);
        return;
      }

      if (this.canAdd(messages)) {
        this.add([messages]);
      }
    });

    this.messageService.clearObserver$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((key) => {
      if (key) {
        if (this.key === key) {
          this.clearMessages();
        }
      } else {
        this.clearMessages();
      }
      this.cdr.markForCheck();
    });
  }

  ngDoCheck(): void {
    if (!this.toastMessages?.length) {
      this.clearTimeouts();
      return;
    }

    this.timeoutByMessage.forEach((timeout, message) => {
      if (!this.toastMessages?.includes(message)) {
        clearTimeout(timeout);
        this.timeoutByMessage.delete(message);
      }
    });

    this.toastMessages.forEach((message) => {
      if (!this.timeoutByMessage.has(message)) {
        this.initTimeout(message);
      }
    });
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.clearContainerZIndex();
  }

  private add(messages: Message[]): void {
    if (!messages.length) {
      return;
    }

    this.toastMessages = this.toastMessages ? [...this.toastMessages, ...messages] : [...messages];
    if (this.preventDuplicates) {
      this.messageArchive = this.messageArchive ? [...this.messageArchive, ...messages] : [...messages];
    }
    messages.forEach((message) => this.initTimeout(message));
    this.cdr.markForCheck();
  }

  private canAdd(message: Message): boolean {
    let allow = this.key === message.key;

    if (allow && this.preventOpenDuplicates) {
      allow = !this.containsMessage(this.toastMessages, message);
    }

    if (allow && this.preventDuplicates) {
      allow = !this.containsMessage(this.messageArchive, message);
    }

    return allow;
  }

  private containsMessage(collection: Message[] | null | undefined, message: Message): boolean {
    if (!collection) {
      return false;
    }

    return collection.some(
      (item) => item.summary === message.summary && item.detail == message.detail && item.severity === message.severity,
    );
  }

  private initTimeout(message: Message): void {
    if (message.sticky) {
      return;
    }

    this.clearTimeout(message);
    this.zone.runOutsideAngular(() => {
      const timeout = setTimeout(
        () => {
          this.zone.run(() => this.removeMessage(message));
        },
        message.life || this.life || this.defaultMessageLifeMs,
      );
      this.timeoutByMessage.set(message, timeout);
    });
  }

  private clearTimeout(message: Message): void {
    const timeout = this.timeoutByMessage.get(message);
    if (!timeout) {
      return;
    }

    clearTimeout(timeout);
    this.timeoutByMessage.delete(message);
  }

  private clearTimeouts(): void {
    this.timeoutByMessage.forEach((timeout) => clearTimeout(timeout));
    this.timeoutByMessage.clear();
  }

  private removeMessage(message: Message): void {
    if (!this.toastMessages?.length) {
      return;
    }

    this.clearTimeout(message);
    const messageIndex = this.toastMessages.indexOf(message);
    if (messageIndex < 0) {
      return;
    }

    this.toastMessages.splice(messageIndex, 1);
    this.cdr.detectChanges();
  }

  private clearMessages(): void {
    this.clearTimeouts();
    this.toastMessages = null;
  }

  private setContainerZIndex(): void {
    const container = this.containerViewChild?.nativeElement;
    if (!container || !this.autoZIndex) {
      return;
    }

    if (container.style.zIndex === '') {
      IndexUtils.set('modal', container, this.baseZIndex || DEFAULT_MODAL_Z_INDEX);
    }
  }

  private clearContainerZIndex(): void {
    const container = this.containerViewChild?.nativeElement;
    if (!container || !this.autoZIndex) {
      return;
    }

    IndexUtils.clear(container);
  }
}
